import * as Yup from 'yup';
import { useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';

import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';

import { baseUrl } from '../../../utils/constants';
import Controls from '../../controls/Controls';
import { useAuthContext } from '../../../context/auth_context';
import Card1 from '../../Card1';

export default function LoginForm() {
  const { setIsAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  const LoginSchema = Yup.object().shape({
    userName: Yup.string()
      .email('Email must be a valid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      userName: '',
      password: '',
      returnUrl: 'ZIMTA.co.zw'
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      setLoading(true);
      handleFormUpdate(values);
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleFormUpdate = (e) => {
    logIn(e);
  };

  const logIn = (loginModel) => {
    const jsonClient = [];
    jsonClient.push(loginModel);
    fetch(`${baseUrl}/api/ServiceAuthentication/ServiceLogin`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jsonClient[0])
    })
      .then((result) => result.json())
      .then(
        (result) => {
          setLoading(false);
          if (result.replyStatus === 0) {
            sessionStorage.setItem('token', result.uniqueIdentifier);
            sessionStorage.setItem('loggedUser', loginModel.userName);
            sessionStorage.setItem('oldRefId', result.transactionRef);
            sessionStorage.setItem('userRefId', result.transactionRef);
            sessionStorage.setItem('dept', result.otherRef);
            sessionStorage.setItem('province', result.groupRef);
            setIsAuthenticated(true);
            getPayCycle();
            setNotify({
              isOpen: true,
              message: 'Logged in successfully!',
              type: 'success'
            });
            navigate('/dashboard', { replace: true });
          } else {
            setIsAuthenticated(false);
            setNotify({
              isOpen: true,
              message: `Error in Logging In!: ${result.replyMessage}`,
              type: 'error'
            });
          }
        },
        (error) => {
          setLoading(false);
          setNotify({
            isOpen: true,
            message: `Error in Logging In!: ${error}`,
            type: 'error'
          });
        }
      );
  };

  const getPayCycle = async () => {
    await axios(`${baseUrl}/api/setting/GetCurrentPayCycle`).then(
      (result) => {
        setLoading(false);
        sessionStorage.setItem('payCycleId', result.data.payCycleId);
      },
      (error) => {
        setNotify({
          isOpen: true,
          message: `Error Getting PayCycle!: ${error}`,
          type: 'error'
        });
      }
    );
  };

  return (
    <FormikProvider value={formik}>
      <Card1>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              autoComplete="userName"
              type="email"
              label="Email address"
              {...getFieldProps('userName')}
              error={Boolean(touched.userName && errors.userName)}
              helperText={touched.userName && errors.userName}
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
            <FormControlLabel
              control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
              label="Remember me"
            />

            <Link component={RouterLink} variant="subtitle2" to="#">
              Forgot password?
            </Link>
          </Stack>

          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={loading}>
            Login
          </LoadingButton>
          <Controls.Notification notify={notify} setNotify={setNotify} />
        </Form>
      </Card1>
    </FormikProvider>
  );
}
